import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Store, Boxes, Pencil, Trash2, Plus, Lock, ArrowLeftRight, Check, X, Send, Receipt, Image as ImageIcon } from "lucide-react";

import { useLojas } from "@/hooks/useLojas";
import { logError, logDebug } from "@/lib/logger";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const hojeISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };

const STATUS_TRANSF = {
  aceita: { rotulo: "Aceita", estilo: "bg-emerald-100 text-emerald-700" },
  recusada: { rotulo: "Recusada", estilo: "bg-red-100 text-red-700" },
  pendente: { rotulo: "Pendente", estilo: "bg-amber-100 text-amber-700" },
};

const TIPOS_TRANSFERIVEIS = ["Armação", "Óculos de Sol"];

const corQuantidade = (qtd) => {
  if (qtd <= 0) return "text-red-600";
  if (qtd <= 3) return "text-amber-600";
  return "text-slate-900";
};

export default function Estoque() {
  const LOJAS = useLojas();
  const [loja, setLoja] = useState(null);
  const [itens, setItens] = useState([]);
  const [matriz, setMatriz] = useState({ lojas: [], itens: [] });
  const [editItem, setEditItem] = useState(null);
  const [novaQtd, setNovaQtd] = useState("");
  const [novoNome, setNovoNome] = useState("");
  const [senha, setSenha] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addNome, setAddNome] = useState("");
  const [addLente, setAddLente] = useState(false);
  const [addSenha, setAddSenha] = useState("");

  const [transferencias, setTransferencias] = useState([]);
  const [transfOpen, setTransfOpen] = useState(false);
  const [transfTipo, setTransfTipo] = useState("");
  const [transfPecaId, setTransfPecaId] = useState("");
  const [transfDestino, setTransfDestino] = useState("");
  const [transfObs, setTransfObs] = useState("");
  const [catalogoPecas, setCatalogoPecas] = useState([]);

  const [vendasCat, setVendasCat] = useState([]);
  const [vendaIni, setVendaIni] = useState(inicioMesISO());
  const [vendaFim, setVendaFim] = useState(hojeISO());
  const [fotoModal, setFotoModal] = useState(null);

  const gerencial = loja === "Gerencial";

  const carregar = useCallback(async (l) => {
    try {
      if (l === "Gerencial") {
        const res = await axios.get(`${API}/estoque/gerencial`);
        setMatriz(res.data || { lojas: [], itens: [] });
      } else {
        const res = await axios.get(`${API}/estoque`, { params: { loja: l } });
        setItens(res.data.itens || []);
      }
    } catch (e) {
      logError("Erro ao carregar estoque:", e);
      toast.error("Erro ao carregar estoque");
    }
  }, []);

  const carregarTransf = useCallback(async (l) => {
    try {
      const params = l && l !== "Gerencial" ? { loja: l } : {};
      const res = await axios.get(`${API}/estoque/transferencias`, { params });
      setTransferencias(res.data.transferencias || []);
    } catch (e) { logDebug("Falha ao carregar transferências:", e); }
  }, []);

  const carregarCatalogoPecas = useCallback(async (l) => {
    try {
      const res = await axios.get(`${API}/catalogo/vitrine`, { params: { loja: l } });
      setCatalogoPecas(res.data.produtos || []);
    } catch (e) { logDebug("Falha ao carregar peças do catálogo:", e); }
  }, []);

  const carregarVendas = useCallback(async (l, ini, fim) => {
    try {
      const params = {};
      if (l && l !== "Gerencial") params.loja = l;
      if (ini) params.inicio = ini;
      if (fim) params.fim = fim;
      const res = await axios.get(`${API}/catalogo/vendas`, { params });
      setVendasCat(res.data.vendas || []);
    } catch (e) { logDebug("Falha ao carregar vendas do catálogo:", e); }
  }, []);

  useEffect(() => { if (loja) { carregar(loja); carregarTransf(loja); } }, [loja, carregar, carregarTransf]);
  useEffect(() => { if (loja) carregarVendas(loja, vendaIni, vendaFim); }, [loja, vendaIni, vendaFim, carregarVendas]);

  const itensComEstoque = useMemo(() => itens.filter((i) => i.controla_estoque), [itens]);
  const lojasDestino = useMemo(() => LOJAS.filter((l) => l !== loja), [LOJAS, loja]);

  const abrirTransferencia = () => {
    setTransfTipo(""); setTransfPecaId(""); setTransfDestino(""); setTransfObs("");
    carregarCatalogoPecas(loja);
    setTransfOpen(true);
  };

  const abrirEdicao = (item, lojaAlvo) => {
    setEditItem({ ...item, lojaAlvo: lojaAlvo || loja });
    setNovaQtd(String(item.quantidade ?? (item.por_loja ? item.por_loja[lojaAlvo] : 0) ?? 0));
    setNovoNome(item.categoria);
    setSenha("");
  };

  const salvarQtd = async () => {
    const q = parseInt(novaQtd, 10);
    if (isNaN(q) || q < 0) { toast.error("Quantidade inválida"); return; }
    if (!senha) { toast.error("Informe a senha de estoque"); return; }
    const alvo = editItem.lojaAlvo;
    try {
      await axios.put(`${API}/estoque`, { loja: alvo, categoria: editItem.categoria, quantidade: q, senha });
      if (!editItem.protegido && novoNome.trim() && novoNome.trim() !== editItem.categoria) {
        await axios.put(`${API}/estoque/produto`, { id: editItem.id, nome: novoNome.trim(), senha });
      }
      toast.success("Estoque atualizado");
      setEditItem(null);
      carregar(loja);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Senha incorreta" : (e.response?.data?.detail || "Erro ao salvar"));
    }
  };

  const excluir = async () => {
    if (!senha) { toast.error("Informe a senha para excluir"); return; }
    try {
      await axios.delete(`${API}/estoque/produto/${editItem.id}`, { params: { senha } });
      toast.success("Produto excluído");
      setEditItem(null);
      carregar(loja);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Senha incorreta" : (e.response?.data?.detail || "Erro ao excluir"));
    }
  };

  const adicionar = async () => {
    if (!addNome.trim()) { toast.error("Informe o nome do produto"); return; }
    if (!addSenha) { toast.error("Informe a senha"); return; }
    try {
      await axios.post(`${API}/estoque/produto`, { nome: addNome.trim(), eh_lente: addLente, senha: addSenha });
      toast.success("Produto adicionado");
      setAddOpen(false); setAddNome(""); setAddLente(false); setAddSenha("");
      carregar(loja);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Senha incorreta" : (e.response?.data?.detail || "Erro ao adicionar"));
    }
  };

  const criarTransferencia = async () => {
    if (!transfTipo) { toast.error("Selecione o tipo (Armação ou Óculos de Sol)"); return; }
    if (!transfDestino) { toast.error("Selecione a loja destino"); return; }
    if (!transfPecaId) { toast.error("Selecione a peça do catálogo"); return; }
    try {
      await axios.post(`${API}/estoque/transferencia`, { produto_id: transfPecaId, tipo: transfTipo, loja_origem: loja, loja_destino: transfDestino, observacao: transfObs });
      toast.success("Peça enviada — aguardando aceite da loja destino");
      setTransfOpen(false); setTransfTipo(""); setTransfPecaId(""); setTransfDestino(""); setTransfObs("");
      carregar(loja); carregarTransf(loja);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao transferir");
    }
  };

  const aceitarTransf = async (id) => {
    try { await axios.post(`${API}/estoque/transferencia/${id}/aceitar`); toast.success("Transferência aceita — estoque atualizado"); carregar(loja); carregarTransf(loja); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao aceitar"); }
  };

  const recusarTransf = async (id) => {
    try { await axios.post(`${API}/estoque/transferencia/${id}/recusar`); toast.success("Transferência recusada — devolvida à origem"); carregar(loja); carregarTransf(loja); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao recusar"); }
  };

  const pendentesEntrada = transferencias.filter((t) => t.status === "pendente" && t.loja_destino === loja);
  const pendentesSaida = transferencias.filter((t) => t.status === "pendente" && t.loja_origem === loja);
  const fmtDataHora = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-";
  const fmtDataVenda = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

  if (!loja) {
    const opcoes = [{ value: "Gerencial", label: "Gerencial (todas as lojas)", gerencial: true }, ...LOJAS.map((l) => ({ value: l, label: l }))];
    return (
      <div className="max-w-2xl mx-auto" data-testid="estoque-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Boxes className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">Estoque — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja ou veja o consolidado gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => setLoja(o.value)} data-testid={`estoque-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"}`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="estoque-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Boxes className="w-7 h-7 text-slate-700" /> Estoque</h1>
          <p className="text-sm text-slate-500">{gerencial ? "Consolidado — todas as lojas" : `Loja: ${loja}`} · baixa automática a cada venda</p>
        </div>
        <div className="flex gap-2">
          {!gerencial && <Button variant="outline" onClick={abrirTransferencia} data-testid="estoque-transferir"><ArrowLeftRight className="w-4 h-4 mr-2" />Transferir</Button>}
          <Button onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="estoque-add-produto"><Plus className="w-4 h-4 mr-2" />Novo produto</Button>
          <Button variant="outline" onClick={() => setLoja(null)} data-testid="estoque-trocar-loja"><Store className="w-4 h-4 mr-2" />Trocar loja</Button>
        </div>
      </div>

      {!gerencial && (pendentesEntrada.length > 0 || pendentesSaida.length > 0) && (
        <div className="mb-6 space-y-3">
          {pendentesEntrada.length > 0 && (
            <Card className="bg-amber-50 border-amber-200" data-testid="estoque-transf-entrada">
              <CardContent className="pt-4">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-3"><ArrowLeftRight className="w-4 h-4" /> Transferências recebidas — aguardando seu aceite</p>
                <div className="space-y-2">
                  {pendentesEntrada.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg border border-amber-200 px-3 py-2" data-testid={`estoque-transf-in-${t.id}`}>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <span className="w-9 h-9 rounded-md border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                          {t.foto_url ? <img src={`${BACKEND}${t.foto_url}`} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
                        </span>
                        <span>
                          <span className="font-semibold">{t.produto_nome}</span>
                          {t.tipo ? <span className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded ml-1">{t.tipo}</span> : null}
                          <span className="text-slate-400"> · de </span><span className="font-medium">{t.loja_origem}</span>
                          {t.observacao ? <span className="text-slate-400 italic"> · {t.observacao}</span> : null}
                          <span className="text-xs text-slate-400 ml-2">{fmtDataHora(t.criado_em)}</span>
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => aceitarTransf(t.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid={`estoque-transf-aceitar-${t.id}`}><Check className="w-4 h-4 mr-1" />Aceitar</Button>
                        <Button size="sm" variant="outline" onClick={() => recusarTransf(t.id)} className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`estoque-transf-recusar-${t.id}`}><X className="w-4 h-4 mr-1" />Recusar</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {pendentesSaida.length > 0 && (
            <Card className="bg-white border-slate-200" data-testid="estoque-transf-saida">
              <CardContent className="pt-4">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Send className="w-4 h-4" /> Enviadas (aguardando aceite do destino)</p>
                <div className="space-y-1">
                  {pendentesSaida.map((t) => (
                    <div key={t.id} className="text-sm text-slate-600 flex items-center gap-2 flex-wrap" data-testid={`estoque-transf-out-${t.id}`}>
                      <span className="font-semibold">{t.produto_nome}</span>
                      <span className="text-slate-400">→ {t.loja_destino}</span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">pendente</span>
                      <span className="text-xs text-slate-400">{fmtDataHora(t.criado_em)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {gerencial ? (
        <div className="space-y-6">
          {/* Card resumo — total da empresa */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="estoque-resumo-empresa">
            <Card className="bg-slate-900 text-white border-slate-900 shadow-sm lg:col-span-1">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs uppercase tracking-wider text-slate-300">Total de produtos da empresa</p>
                <p className="text-4xl font-bold mt-1" data-testid="estoque-empresa-total">{matriz.itens.reduce((s, it) => s + it.total, 0)}</p>
                <p className="text-xs text-slate-400 mt-1">Somatório de todas as lojas</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm lg:col-span-2">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Total por loja</p>
                <div className="flex flex-wrap gap-2">
                  {matriz.lojas.map((l) => (
                    <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-w-[120px]" data-testid={`estoque-resumo-loja-${l}`}>
                      <p className="text-xs font-medium text-slate-500 truncate">{l}</p>
                      <p className="text-xl font-bold text-slate-900">{matriz.itens.reduce((s, it) => s + (it.por_loja[l] || 0), 0)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

        <Card className="bg-white border-slate-200 shadow-sm" data-testid="estoque-gerencial">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800 hover:bg-slate-800">
                  <TableHead className="text-white font-semibold">Produto</TableHead>
                  {matriz.lojas.map((l) => (<TableHead key={l} className="text-center text-white font-semibold whitespace-nowrap">{l}</TableHead>))}
                  <TableHead className="text-center text-white font-semibold">Total</TableHead>
                  <TableHead className="text-right text-white font-semibold">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matriz.itens.map((it, idx) => (
                  <TableRow key={it.id} className={idx % 2 ? "bg-slate-50/60" : "bg-white"} data-testid={`estoque-grow-${it.categoria}`}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-1">{it.protegido && <Lock className="w-3 h-3 text-slate-400" />}{it.categoria}{it.eh_lente && <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">lente</span>}</span>
                    </TableCell>
                    {matriz.lojas.map((l) => (
                      <TableCell key={l} className="text-center">
                        <button onClick={() => abrirEdicao({ id: it.id, categoria: it.categoria, protegido: it.protegido, quantidade: it.por_loja[l] }, l)}
                          className={`px-2 py-0.5 rounded hover:bg-slate-100 font-semibold ${it.por_loja[l] <= 0 ? "text-red-600" : "text-slate-800"}`}
                          data-testid={`estoque-gcell-${it.categoria}-${l}`} title="Clique para ajustar">
                          {it.por_loja[l]}
                        </button>
                      </TableCell>
                    ))}
                    <TableCell className="text-center font-bold text-slate-900">{it.total}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => abrirEdicao({ id: it.id, categoria: it.categoria, protegido: it.protegido, quantidade: it.por_loja[matriz.lojas[0]] }, matriz.lojas[0])} data-testid={`estoque-gedit-${it.categoria}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell>Total de peças</TableCell>
                  {matriz.lojas.map((l) => (
                    <TableCell key={l} className="text-center" data-testid={`estoque-gtotal-${l}`}>{matriz.itens.reduce((s, it) => s + (it.por_loja[l] || 0), 0)}</TableCell>
                  ))}
                  <TableCell className="text-center text-slate-900" data-testid="estoque-gtotal-geral">{matriz.itens.reduce((s, it) => s + it.total, 0)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Relatório de transferências (gerencial) */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="estoque-transf-relatorio">
          <CardContent className="pt-5">
            <p className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-3"><ArrowLeftRight className="w-4 h-4 text-slate-500" /> Relatório de transferências</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead>Data</TableHead><TableHead>Produto</TableHead><TableHead>Origem → Destino</TableHead>
                    <TableHead className="text-center">Qtd</TableHead><TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transferencias.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Nenhuma transferência registrada</TableCell></TableRow>}
                  {transferencias.map((t) => (
                    <TableRow key={t.id} data-testid={`estoque-transf-rel-${t.id}`}>
                      <TableCell className="text-slate-600 whitespace-nowrap">{fmtDataHora(t.criado_em)}</TableCell>
                      <TableCell className="font-medium">{t.produto_nome}</TableCell>
                      <TableCell className="whitespace-nowrap">{t.loja_origem} <span className="text-slate-400">→</span> {t.loja_destino}</TableCell>
                      <TableCell className="text-center font-semibold">{t.quantidade}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_TRANSF[t.status]?.estilo || STATUS_TRANSF.pendente.estilo}`}>
                          {STATUS_TRANSF[t.status]?.rotulo || STATUS_TRANSF.pendente.rotulo}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {itens.map((it) => (
            <Card key={it.id} className="bg-white border-slate-200 shadow-sm" data-testid={`estoque-card-${it.categoria}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1 truncate">
                      {it.protegido && <Lock className="w-3 h-3 shrink-0" />}{it.categoria}
                      {it.eh_lente && <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">lente</span>}
                    </p>
                    <p className={`text-3xl font-bold mt-1 ${corQuantidade(it.quantidade)}`} data-testid={`estoque-qtd-${it.categoria}`}>
                      {it.quantidade}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{it.controla_estoque ? "unidades" : "não controla estoque"}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => abrirEdicao(it)} data-testid={`estoque-edit-${it.categoria}`}>
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </Button>
                </div>
                {it.controla_estoque && it.quantidade <= 0 && <span className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Sem estoque</span>}
                {it.controla_estoque && it.quantidade > 0 && it.quantidade <= 3 && <span className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Estoque baixo</span>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Relatório de peças vendidas (catálogo) */}
      <Card className="bg-white border-slate-200 shadow-sm mt-6" data-testid="estoque-vendas-relatorio">
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-base font-semibold text-slate-800 flex items-center gap-2"><Receipt className="w-4 h-4 text-slate-500" /> Peças vendidas (catálogo){gerencial ? "" : ` — ${loja}`}</p>
            <div className="flex items-end gap-2">
              <div><Label className="text-xs text-slate-500">De</Label><Input type="date" value={vendaIni} onChange={(e) => setVendaIni(e.target.value)} className="h-9" data-testid="estoque-vendas-ini" /></div>
              <div><Label className="text-xs text-slate-500">Até</Label><Input type="date" value={vendaFim} onChange={(e) => setVendaFim(e.target.value)} className="h-9" data-testid="estoque-vendas-fim" /></div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Peça</TableHead><TableHead>Referência</TableHead><TableHead>OS</TableHead>
                  <TableHead>Cliente</TableHead><TableHead>Loja</TableHead><TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendasCat.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nenhuma peça vendida no período</TableCell></TableRow>}
                {vendasCat.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => v.foto_url && setFotoModal(v)} data-testid={`estoque-venda-${v.id}`}>
                    <TableCell>
                      <span className="flex items-center gap-2 font-medium text-slate-800">
                        <span className="w-9 h-9 rounded-md border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                          {v.foto_url ? <img src={`${BACKEND}${v.foto_url}`} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
                        </span>
                        {v.nome}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">{v.codigo || "-"}</TableCell>
                    <TableCell className="text-slate-600">{v.os || "-"}</TableCell>
                    <TableCell className="text-slate-600">{v.nome_cliente || "-"}</TableCell>
                    <TableCell className="text-slate-600">{v.loja}</TableCell>
                    <TableCell className="text-slate-600 whitespace-nowrap">{fmtDataVenda(v.data)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-slate-400 mt-2">Clique em uma peça para ver a foto do catálogo · Total: {vendasCat.length}</p>
        </CardContent>
      </Card>

      {/* Modal foto da peça vendida */}
      <Dialog open={!!fotoModal} onOpenChange={(o) => !o && setFotoModal(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="estoque-venda-foto-modal">
          <DialogHeader>
            <DialogTitle>{fotoModal?.nome}</DialogTitle>
            <DialogDescription>{fotoModal?.codigo ? `Ref.: ${fotoModal.codigo} · ` : ""}{fotoModal?.loja}{fotoModal?.os ? ` · OS ${fotoModal.os}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="w-full aspect-square rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
            {fotoModal?.foto_url ? <img src={`${BACKEND}${fotoModal.foto_url}`} alt={fotoModal?.nome} className="w-full h-full object-contain" /> : <ImageIcon className="w-10 h-10 text-slate-300" />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal editar / renomear / excluir */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="estoque-modal">
          <DialogHeader>
            <DialogTitle>Ajustar — {editItem?.categoria}</DialogTitle>
            <DialogDescription>Quantidade em <b>{editItem?.lojaAlvo}</b>. {editItem?.protegido ? "Produto base (nome não editável)." : "Você pode renomear ou excluir."} Requer senha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editItem?.protegido && (
              <div><Label>Nome do produto</Label><Input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} data-testid="estoque-input-nome" /></div>
            )}
            <div><Label>Quantidade</Label><Input type="number" min="0" value={novaQtd} onChange={(e) => setNovaQtd(e.target.value)} data-testid="estoque-input-qtd" /></div>
            <div><Label>Senha de estoque</Label><Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Senha" data-testid="estoque-input-senha" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!editItem?.protegido && (
              <Button variant="destructive" onClick={excluir} className="sm:mr-auto" data-testid="estoque-excluir"><Trash2 className="w-4 h-4 mr-1" />Excluir</Button>
            )}
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancelar</Button>
            <Button onClick={salvarQtd} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="estoque-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal adicionar produto */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="estoque-add-modal">
          <DialogHeader>
            <DialogTitle>Novo produto</DialogTitle>
            <DialogDescription>O produto ficará disponível para seleção na venda e dará baixa automática por loja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome do produto</Label><Input value={addNome} onChange={(e) => setAddNome(e.target.value)} placeholder="Ex.: Lente Antirreflexo" data-testid="estoque-add-nome" /></div>
            <div className="flex items-center gap-2">
              <Checkbox id="add-lente" checked={addLente} onCheckedChange={setAddLente} data-testid="estoque-add-lente" />
              <Label htmlFor="add-lente" className="text-sm text-slate-600">É uma lente (mostra grau/medidas na venda)</Label>
            </div>
            <div><Label>Senha de estoque</Label><Input type="password" value={addSenha} onChange={(e) => setAddSenha(e.target.value)} placeholder="Senha" data-testid="estoque-add-senha" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={adicionar} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="estoque-add-salvar">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal transferência de peça do catálogo entre lojas */}
      <Dialog open={transfOpen} onOpenChange={setTransfOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="estoque-transf-modal">
          <DialogHeader>
            <DialogTitle>Transferir peça do catálogo</DialogTitle>
            <DialogDescription>De <b>{loja}</b> para outra loja. A peça sai agora do catálogo e do estoque de {loja} e só entra no destino após o aceite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={transfTipo} onValueChange={setTransfTipo}>
                  <SelectTrigger data-testid="estoque-transf-tipo"><SelectValue placeholder="Armação / Óculos de Sol" /></SelectTrigger>
                  <SelectContent>{TIPOS_TRANSFERIVEIS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Loja destino</Label>
                <Select value={transfDestino} onValueChange={setTransfDestino}>
                  <SelectTrigger data-testid="estoque-transf-destino"><SelectValue placeholder="Selecione a loja" /></SelectTrigger>
                  <SelectContent>{lojasDestino.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Peça do catálogo de {loja}</Label>
              {catalogoPecas.length === 0 ? (
                <p className="text-sm text-slate-400 mt-1">Nenhuma peça disponível no catálogo desta loja.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 mt-1 max-h-64 overflow-y-auto p-1" data-testid="estoque-transf-pecas">
                  {catalogoPecas.map((p) => (
                    <button key={p.id} type="button" onClick={() => setTransfPecaId(p.id)}
                      className={`rounded-lg border p-1.5 text-left transition-all ${transfPecaId === p.id ? "border-slate-900 ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`estoque-transf-peca-${p.id}`}>
                      <div className="w-full aspect-square rounded-md bg-white border border-slate-100 flex items-center justify-center overflow-hidden">
                        {p.foto_url ? <img src={`${BACKEND}${p.foto_url}`} alt={p.nome} className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                      </div>
                      <p className="text-[11px] font-medium text-slate-700 truncate mt-1">{p.nome}</p>
                      {p.codigo ? <p className="text-[10px] text-slate-400 truncate">{p.codigo}</p> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div><Label>Observação (opcional)</Label><Textarea value={transfObs} onChange={(e) => setTransfObs(e.target.value)} placeholder="Ex: pedido do cliente, reposição" data-testid="estoque-transf-obs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransfOpen(false)}>Cancelar</Button>
            <Button onClick={criarTransferencia} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="estoque-transf-enviar"><Send className="w-4 h-4 mr-1" />Enviar transferência</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
